import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// 議事録一覧取得用
type GetMinutesResponse = {
  minutes_list: Minute[];
};

type Minute = {
  id: string;
  title: string;
  meeting_timestamp: number;
  company_name: string;
  content: string;
  updated_timestamp: number;
  created_timestamp: number;
  issue_action: IssueAction[];
};

// 議事録登録用
type PostMinuteInput = {
  title: string;
  meeting_timestamp: number | null;
  company_name: string | null;
  content: string | null;
  issue_action: IssueAction[];
};

type PostMinuteResponse = {
  id: string;
};
// 議事録取得用(1件)
type GetOneMinuteResponse = {
  id: string;
  title: string;
  meeting_timestamp: number;
  company_name: string;
  content: string;
  updated_timestamp: number;
  created_timestamp: number;
  issue_action: IssueAction[];
};

// 議事録更新用
type PutMinuteInput = {
  memoid: string;
  title: string | null;
  meeting_timestamp: number | null;
  company_name: string | null;
  content: string | null;
  issue_action: IssueAction[];
};

type PutMinuteResponse = {
  id: string;
  title: string;
  meeting_timestamp: number;
  company_name: string;
  content: string;
  updated_timestamp: number;
  created_timestamp: number;
  issue_action: IssueAction[];
};

// 議事録削除用
type DeleteMinuteResponse = {
  status: number;
};

// 生成履歴取得用
type GetSearchHistoryResponse = {
  keywords_history: string[];
};

// フィードバック更新用
type PutFeedbackInput = {
  id: string;
  search_id: string;
  idx: string;
  keyword: string;
  feedback: number;
};

type PutFeedbackResponse = {
  id: string;
  search_id: string;
  idx: string;
  keyword: string;
  feedback: number;
};

// フィードバック履歴ダウンロード用
type GetFeedbackInput = {
  id: string;
  search_id: string;
  title: string;
  meeting_timestamp: string;
  company_name: string;
};

type GetFeedbackResponse = {
  url: string;
};

// 課題アクション取得用
type Action = {
  action: string;
};

type IssueAction = {
  issue: string;
  actions: Action[];
};

// 課題アクション生成用
type PostIssueActionInput = {
  memo_id: string;
  content: string | null;
};

type PostIssueActionResponse = {
  execute_id: string;
};

type GetIssueActionResponse = {
  status: string;
  execute_id: string;
  extended_info: {
    memo_id: string;
  };
  issue_action: {
    issue: string;
    actions: { action: string }[];
  }[];
};

export type GetClusteringResponse = {
  clusters: Clusters[];
  next_key: string;
};

export type Clusters = {
  cluster_id: number;
  cluster_title: string;
  industory_id: number;
  data_source: string;
  cluster_score: number;
  issues: Issues[];
};

export type Issues = {
  id: number;
  issue_name: string;
  issue_score: number;
  url: string;
  similar_company_issues: SimilarCompanyIssue[];
};

export type SimilarCompanyIssue = {
  id: number;
  issue_name: string;
  company_score: number;
  company_name: string;
  JSIC: string;
  doc_id: string;
};

export type GetCauseResponse = {
  assignment_cause: {
    assignment: string;
    cause_list: { id: string; cause: string }[];
  };
};

export type GetMinutesIssues = {
  results: MinutesIssues[];
};

export type MinutesIssues = {
  minute_id: string;
  issue: string;
  similarity_score: number;
  company_name: string;
  meeting_timestamp: number;
};

console.log(process.env.REACT_APP_ENV_INFO);
console.log(process.env.REACT_APP_BASE_API);

export const baseApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_API,
    prepareHeaders: async (headers, _) => {
      const queryParams = new URLSearchParams(window.location.search);
      const idToken = queryParams.get("idToken");
      const postMessageHandler = (e: any) => {
        if (e.data.type && "postIdToken" === e.data.type) {
          console.log("postMessageを受け取ったイベント情報：", e.data);
          localStorage.setItem("idToken", `${e.data.idToken}`);
        }
      };
      window.addEventListener("message", postMessageHandler);

      if (idToken) {
        headers.set("Authorization", `${idToken}`);
        localStorage.setItem("idToken", `${idToken}`);
      } else {
        const idToken = localStorage.getItem("idToken");
        headers.set("Authorization", `${idToken}`);
      }
      return headers;
    },
    // mode: "cors",
  }),
  endpoints: (builder) => ({
    getMinutes: builder.query<GetMinutesResponse, string>({
      query: (search) => {
        if (search.length !== 0) {
          return `minutes?search=${encodeURIComponent(search)}`;
        } else {
          return `minutes`;
        }
      },
    }),
    addMinute: builder.mutation<PostMinuteResponse, PostMinuteInput>({
      query(body) {
        return {
          url: `minutes`,
          method: "POST",
          body: body,
        };
      },
    }),
    getOneMinute: builder.query<GetOneMinuteResponse, string>({
      query: (id) => `minutes/${encodeURIComponent(id)}`,
    }),
    updateMinute: builder.mutation<PutMinuteResponse, PutMinuteInput>({
      query(data) {
        const { memoid, ...body } = data;
        return {
          url: `minutes/${encodeURIComponent(memoid)}`,
          method: "PUT",
          body,
        };
      },
    }),
    deleteMinute: builder.mutation<DeleteMinuteResponse, string>({
      query(id) {
        return {
          url: `minutes/${encodeURIComponent(id)}`,
          method: "DELETE",
        };
      },
    }),
    updateFeedback: builder.mutation<PutFeedbackResponse, PutFeedbackInput>({
      query(data) {
        const { id, search_id, idx, ...body } = data;
        return {
          url: `minutes/${encodeURIComponent(id)}/${encodeURIComponent(
            search_id
          )}/${encodeURIComponent(idx)}/feedback`,
          method: "PUT",
          body,
        };
      },
    }),
    getSearchHistory: builder.query<GetSearchHistoryResponse, string>({
      query: (id) => `minutes/${encodeURIComponent(id)}/search-histories`,
    }),
    getFeedback: builder.query<GetFeedbackResponse, GetFeedbackInput>({
      query: (data) => {
        const { id, search_id, title, meeting_timestamp, company_name } = data;
        return {
          url: `minutes/${encodeURIComponent(id)}/${encodeURIComponent(
            search_id
          )}/search-histories?title=${encodeURIComponent(
            title
          )}&meeting_timestamp=${encodeURIComponent(
            meeting_timestamp
          )}&company_name=${encodeURIComponent(company_name)}`,
          method: "GET",
        };
      },
    }),
    getClustering: builder.query<
      GetClusteringResponse,
      {
        keyword: string;
        industry_id?: string;
        cluster_filter?: number;
        issue_filter?: number;
        next_key?: string;
      }
    >({
      query: ({
        keyword,
        industry_id = "",
        cluster_filter = 0.7,
        issue_filter = 0.7,
        next_key = "",
      }) => {
        const params = new URLSearchParams({
          keyword,
          industry_id,
          cluster_filter: cluster_filter.toString(),
          issue_filter: issue_filter.toString(),
          next_key,
        });
        console.log("getClustering:", builder);
        return `clustering?${params.toString()}`;
      },
    }),
    getCause: builder.query<GetCauseResponse, { assignment_word: string }>({
      query: ({ assignment_word }) => {
        const params = new URLSearchParams({ assignment_word });
        return {
          url: `cause?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    generateIssueAction: builder.mutation<
      PostIssueActionResponse,
      PostIssueActionInput
    >({
      query(body) {
        return {
          url: `issue_action`,
          method: "POST",
          body,
        };
      },
    }),
    getIssueAction: builder.query<GetIssueActionResponse, string>({
      query: (executeId) => `issue_action/${encodeURIComponent(executeId)}`,
    }),
    getMinutesIssue: builder.query<
      GetMinutesIssues,
      {
        keyword: string;
        issue_filter?: number;
      }
    >({
      query: ({ keyword, issue_filter = 0.7 }) => {
        const params = new URLSearchParams({
          keyword,
          score_threshold: issue_filter.toString(),
        });
        console.log("getMinutesIssue:", builder);
        return {
          url: `minutes/search-similar?${params.toString()}`,
          // url: `minutes/search-similar`,
          method: "GET",
        };
      },
    }),
  }),
});

// use + endpointsで設定した名前 + QueryでHooksが作られる
export const {
  useLazyGetMinutesQuery,
  useAddMinuteMutation,
  useGetOneMinuteQuery,
  useUpdateMinuteMutation,
  useDeleteMinuteMutation,
  useUpdateFeedbackMutation,
  useGetSearchHistoryQuery,
  useLazyGetFeedbackQuery,
  useLazyGetClusteringQuery,
  useLazyGetMinutesIssueQuery,
  useLazyGetCauseQuery,
  useGenerateIssueActionMutation,
  useLazyGetIssueActionQuery,
} = baseApi;
